import { useEffect, useState } from "react";
import axios from "axios";
import { TIRAMISUS } from "../data/content";
import { Plus } from "lucide-react";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { SectionClouds } from "./CloudCanvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mergeProducts = (apiProducts) =>
  TIRAMISUS.map((staticP) => {
    const dynP = apiProducts.find((x) => x.id === staticP.id);
    return dynP ? { ...staticP, price: dynP.price, in_stock: dynP.in_stock, name: dynP.name } : staticP;
  });

// 3D tilt card with mouse-follow + parallax inner image
const TiltCard = ({ product, idx, outOfStock }) => {
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 0.5, my: 0.5 });

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({
      rx: -(y - 0.5) * 12,
      ry: (x - 0.5) * 12,
      mx: x,
      my: y,
    });
  };
  const onLeave = () => setTilt({ rx: 0, ry: 0, mx: 0.5, my: 0.5 });

  return (
    <article
      data-testid={`gourmandises-card-${product.id}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`group relative ${product.bg} rounded-[32px] p-6 cursor-pointer ${
        outOfStock ? "opacity-70" : ""
      }`}
      style={{
        transform: `perspective(1000px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
        transformStyle: "preserve-3d",
        transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease",
        boxShadow: tilt.rx || tilt.ry
          ? `0 ${30 + Math.abs(tilt.rx) * 2}px ${60 + Math.abs(tilt.ry) * 2}px rgba(229, 91, 91, 0.18)`
          : "0 8px 24px rgba(42,36,34,0.06)",
        animationDelay: `${idx * 80}ms`,
      }}
    >
      {/* Specular highlight following mouse */}
      <div
        className="absolute inset-0 rounded-[32px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(400px circle at ${tilt.mx * 100}% ${tilt.my * 100}%, rgba(255,255,255,0.5), transparent 50%)`,
        }}
      />

      {outOfStock ? (
        <span
          className="absolute top-5 right-5 bg-coral text-white text-[11px] uppercase tracking-[0.16em] font-medium pill px-3 py-1 z-10"
          style={{ transform: "translateZ(40px)" }}
        >
          Rupture
        </span>
      ) : (
        product.badge && (
          <span
            className="absolute top-5 right-5 bg-white text-coral text-[11px] uppercase tracking-[0.16em] font-medium pill px-3 py-1 shadow-sm z-10"
            style={{ transform: "translateZ(40px)" }}
          >
            {product.badge}
          </span>
        )
      )}

      <div
        className="aspect-[4/3] rounded-2xl overflow-hidden bg-white/40 mb-5 relative"
        style={{ transform: "translateZ(30px)" }}
      >
        <img
          src={product.image}
          alt={`${product.name} — dessert artisanal fait maison Amyu, Carcassonne`}
          loading="lazy"
          decoding="async"
          className={`w-full h-full object-cover ${outOfStock ? "grayscale" : ""}`}
          style={{
            transform: `scale(1.08) translate3d(${(tilt.mx - 0.5) * -20}px, ${(tilt.my - 0.5) * -20}px, 0)`,
            transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </div>

      <div
        className="flex items-start justify-between gap-3"
        style={{ transform: "translateZ(20px)" }}
      >
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-2xl md:text-3xl text-amyu-ink leading-tight">
            {product.name}
          </h3>
          <p className="mt-2 text-sm text-amyu-muted">{product.tagline}</p>
        </div>
        {product.price != null && (
          <span
            data-testid={`gourmandises-price-${product.id}`}
            className="font-display text-2xl text-coral whitespace-nowrap"
          >
            {product.price.toFixed(2).replace(".", ",")} €
          </span>
        )}
      </div>

      <a
        href="#commander"
        data-testid={`gourmandises-order-${product.id}`}
        className={`mt-5 inline-flex items-center gap-2 text-sm font-medium transition-colors ${
          outOfStock ? "text-amyu-muted pointer-events-none" : "text-amyu-ink hover:text-coral"
        }`}
        style={{ transform: "translateZ(25px)" }}
      >
        <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm group-hover:bg-coral group-hover:text-white transition-all">
          <Plus size={16} />
        </span>
        {outOfStock ? "Bientôt de retour" : "Ajouter à ma commande"}
      </a>
    </article>
  );
};

export const Gourmandises = () => {
  const [products, setProducts] = useState(TIRAMISUS);
  const { ref, visible } = useScrollReveal();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(mergeProducts(r.data.products || [])))
      .catch(() => {});
  }, []);

  return (
    <section
      id="gourmandises"
      data-testid="gourmandises-section"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <SectionClouds
        clouds={[
          { top: "5%", left: "70%", size: 280, opacity: 0.8, blur: 28, duration: 15 },
          { top: "45%", left: "-4%", size: 220, opacity: 0.7, blur: 24, duration: 13, delay: 2 },
          { bottom: "8%", right: "8%", size: 240, opacity: 0.75, blur: 26, duration: 17, delay: 4 },
        ]}
      />
      <div className="max-w-7xl mx-auto px-6">
        <div
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 transition-all duration-1000"
          style={{
            opacity: visible ? 1 : 0,
            transform: `translateY(${visible ? 0 : 40}px)`,
          }}
        >
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-coral font-medium">
              Nos gourmandises
            </span>
            <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] max-w-2xl">
              Les douceurs qu'on
              <br />
              <span className="italic text-coral">prépare avec passion.</span>
            </h2>
          </div>
          <p className="font-hand text-2xl text-amyu-muted max-w-sm">
            survole une carte — elle s'incline pour toi.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          {products.map((p, idx) => (
            <div
              key={p.id}
              className="transition-all duration-700"
              style={{
                opacity: visible ? 1 : 0,
                transform: `translateY(${visible ? 0 : 60}px)`,
                transitionDelay: `${idx * 100 + 200}ms`,
              }}
            >
              <TiltCard product={p} idx={idx} outOfStock={p.in_stock === false} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
